import { useEffect, useState } from 'react';
import { AlignTypes } from 'ui/tableHeader';
import { TableHeaderCell } from 'ui/tableHeaderCell';

import { selectFeatureFlags } from 'core/userContext';
import { getIsEligibilityCampaign } from 'modules/campaigns/editor/components/utils';
import { useCampaignContext } from 'modules/campaigns/editor/context';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import {
  TABLE_GRID,
  TABLE_GRID_ELIGIBILITY,
  TableHeader,
  TableWrapper,
} from '../../AudienceSelection.styled';
import { useAudienceContext } from '../../context';
import { TableEmptyState } from '../EmptyStates';
import { AudienceListSummary } from './AudienceListSummary/AudienceListSummary';
import { AudienceSelectionSkeletonRow } from './AudienceSelectionSkeletonRow';
import { AudienceSelectionTableRow } from './AudienceSelectionTableRow';

export const AUDIENCE_TABLE_TEST_ID = 'audience-table';
export const AUDIENCE_TABLE_HEADER_TEST_ID = 'audience-table-header';

const CENTER = 'center' as AlignTypes;
const FLEX_START = 'flex-start' as AlignTypes;

export enum TABLE_HEADER_COL_VALUES {
  Send = 'Send',
  Suppress = 'Suppress',
  Eligible = 'Eligible',
  Name = 'Name',
  Id = 'Id',
  ListType = 'ListType',
  Size = 'Size',
  UpdatedAt = 'UpdatedAt',
}

const SORTABLE_COLS = [TABLE_HEADER_COL_VALUES.Name, TABLE_HEADER_COL_VALUES.UpdatedAt];

const AudienceSelectionTableWrapper = styled.div`
  display: flex;
  border-radius: ${spacing.half};
  border: ${spacing.single} solid ${colors.grey15};
  overflow: hidden;
  margin-top: ${spacing.plusHalf};
`;

export const AudienceSelectionTable = () => {
  const canFetchListSize = !useSelector(selectFeatureFlags).DisableListsSizeQueryFetch;
  const {
    isLoading,
    lists,
    sort: { sortBy, sortDirection },
    pagination,
    handleSort,
  } = useAudienceContext();
  const { campaign } = useCampaignContext();
  const isEligibility = getIsEligibilityCampaign(campaign.campaignTypeDefinition);
  const { limit, count } = pagination;

  const [numLoadingRows, setNumLoadingRows] = useState(limit);
  const noResults = !isLoading && !count;

  const HEADER_COLS = [
    ...(isEligibility
      ? [{ align: CENTER, name: 'Eligible', value: TABLE_HEADER_COL_VALUES.Eligible }]
      : [
          { align: CENTER, name: 'Send', value: TABLE_HEADER_COL_VALUES.Send },
          { align: CENTER, name: 'Suppress', value: TABLE_HEADER_COL_VALUES.Suppress },
        ]),
    { align: FLEX_START, name: 'Name', value: TABLE_HEADER_COL_VALUES.Name },
    { align: FLEX_START, name: 'List ID', value: TABLE_HEADER_COL_VALUES.Id },
    { align: FLEX_START, name: 'Type', value: TABLE_HEADER_COL_VALUES.ListType },
    ...(canFetchListSize
      ? [{ align: FLEX_START, name: 'Contacts', value: TABLE_HEADER_COL_VALUES.Size }]
      : []),
    { align: FLEX_START, name: 'Last modified', value: TABLE_HEADER_COL_VALUES.UpdatedAt },
  ];
  const gridLayout = isEligibility ? TABLE_GRID_ELIGIBILITY : TABLE_GRID;

  useEffect(() => {
    // for smoother UX, set the number of loading rows to be that of the previous number of lists
    const numLists = lists?.length;
    if (numLists > 0) {
      setNumLoadingRows(numLists);
    }
  }, [lists]);

  const AudienceSelectionTableHeader = HEADER_COLS.map((col) => {
    const { value } = col;
    const isSortable = SORTABLE_COLS.includes(value);
    return (
      <TableHeaderCell
        key={value}
        data-test={`${value}-header-cell`}
        columnData={col}
        handleClick={() => {
          if (isSortable) {
            handleSort(value);
          }
        }}
        isActiveSort={value === sortBy}
        sortable={isSortable}
        sortDirection={sortDirection}
      />
    );
  });

  return (
    <AudienceSelectionTableWrapper>
      <TableWrapper $isExpanded={noResults} data-test={AUDIENCE_TABLE_TEST_ID}>
        <TableHeader gridLayout={gridLayout} data-test={AUDIENCE_TABLE_HEADER_TEST_ID}>
          {AudienceSelectionTableHeader}
        </TableHeader>
        {noResults && <TableEmptyState />}
        {isLoading
          ? [...Array(numLoadingRows)].map((_, i) => <AudienceSelectionSkeletonRow key={i} />)
          : lists?.map((list) => <AudienceSelectionTableRow key={list.id} list={list} />)}
      </TableWrapper>
      <AudienceListSummary />
    </AudienceSelectionTableWrapper>
  );
};
